import { PHASE_LABELS } from '~/models/phase'
import type { PhaseEndState } from '~/services/phaseService'
import { formatCalendarDate, formatDayCount } from '~/utils/format'
import styles from './PhaseEndNotice.module.css'

/**
 * The planned-end warning (D-020).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NOTHING HAS CHANGED, AND THE COPY HAS TO SAY SO.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The single most damaging thing this notice could do is imply that the phase,
 * the diet or the targets moved on by themselves. They did not: reaching a
 * planned end changes nothing at all until the user decides, and every word here
 * is chosen so that a person skim-reading it on a phone cannot conclude
 * otherwise.
 *
 * It is also **neutral**. Passing a planned end is not a failure, not a missed
 * deadline and not a lapse — the quality bar's non-punitive rule applies with
 * more force here than anywhere else, because this is the moment a user is most
 * likely to read judgement into a screen.
 */

interface PhaseEndNoticeProps {
  state: PhaseEndState
  /** Shown only where the decision cannot be made in place, i.e. on Today. */
  onGoToDecision?: () => void
}

export function PhaseEndNotice({ state, onGoToDecision }: PhaseEndNoticeProps) {
  const { phase } = state
  if (phase === undefined || !state.reachedPlannedEnd) return null

  const elapsed =
    state.daysPastPlannedEnd === 0
      ? 'today'
      : `${formatDayCount(state.daysPastPlannedEnd)} ago, on ${formatCalendarDate(phase.plannedEndDate)}`

  return (
    <section class={styles.notice} aria-labelledby="phase-end-heading" role="status">
      <h2 id="phase-end-heading" class={styles.heading}>
        {PHASE_LABELS[phase.type]} reached its planned end
      </h2>

      <p class={styles.body}>
        Your planned end was {elapsed}. <strong>Nothing has changed</strong> — your phase, its
        targets and your weekly recommendation are all exactly as they were, and they will stay that
        way until you decide what happens next.
      </p>

      <p class={styles.options}>
        You can end this phase, extend it, or leave it running while you think about it. There is no
        wrong answer and no hurry.
      </p>

      {onGoToDecision === undefined ? null : (
        <button type="button" class={styles.action} onClick={onGoToDecision}>
          Decide in Settings
        </button>
      )}
    </section>
  )
}
